import type { ReactNode } from "react";
import { flow } from "lodash";
import { useShouldShowLevelFilter } from "@circle-react-shared/MemberFilters/useShouldShowLevelFilter";
import { DynamicFilters } from "./DynamicFilters";
import { FilterValueContextProvider } from "./FilterValueContext";
import { ProfileFieldFilter } from "./ProfileFieldFilter";
import type { RenderFilter } from "./types";
import { useMemberFilterUrl } from "./useMemberFilterUrlV2";
import { usePaywallFilter } from "./usePaywallFilter";
import { useProfileFieldFilters } from "./useProfileFieldFilters";
import { useShouldShowActivityScoreFilter } from "./useShouldShowActivityScoreFilter";
import { useShouldShowAvatarSetFilter } from "./useShouldShowAvatarSetFilter";
import { useShouldShowMemberDaysAgoFilters } from "./useShouldShowDaysAgoFilter";
import { useShouldShowInvitationLinksFilter } from "./useShouldShowInvitationLinksFilter";
import { useShouldShowMarketingFilters } from "./useShouldShowMarketingFilters";
import { useShouldShowMemberTagsFilter } from "./useShouldShowMemberTagsFilter";
import { useShouldShowNearMeFilter } from "./useShouldShowNearMeFilter";

interface MemberFiltersProps {
  children: ReactNode;
  className?: string;
  defaultFilterNames: string[];
  persistentFilterNames?: string[];
  platformFilters: RenderFilter[];
}

export const MemberFilters = ({
  platformFilters = [],
  defaultFilterNames,
  persistentFilterNames = [],
  className,
  children,
}: MemberFiltersProps) => {
  const { filterParams, updateUrl, removeFilterFromUrl } = useMemberFilterUrl();
  const {
    isLoadingProfileFields,
    customProfileFields,
    filteredDefaultFilters,
  } = useProfileFieldFilters({
    platformFilters,
    component: ProfileFieldFilter,
  });

  const marketingFiltered = flow(
    usePaywallFilter,
    useShouldShowMemberDaysAgoFilters,
    useShouldShowAvatarSetFilter,
    useShouldShowMemberTagsFilter,
    useShouldShowInvitationLinksFilter,
    useShouldShowLevelFilter,
    useShouldShowMarketingFilters,
    useShouldShowNearMeFilter,
  )(filteredDefaultFilters);
  const finalFilters = useShouldShowActivityScoreFilter({
    filters: marketingFiltered,
    canShowActivityScoreDisabled: true,
  });

  if (isLoadingProfileFields) {
    return null;
  }

  return (
    <FilterValueContextProvider
      filterParams={filterParams}
      onApply={updateUrl}
      onClearAll={removeFilterFromUrl}
    >
      <DynamicFilters
        defaultFilterNames={defaultFilterNames}
        persistentFilterNames={persistentFilterNames}
        platformFilters={finalFilters}
        profileFieldFilters={customProfileFields}
        className={className}
      >
        <DynamicFilters.Panel>{children}</DynamicFilters.Panel>
        <DynamicFilters.ClearButton />
      </DynamicFilters>
    </FilterValueContextProvider>
  );
};
